import { ReceiptOfGoodsService } from "services/receipt-of-goods-service";
import { I18N } from "aurelia-i18n";
import { autoinject, bindable } from "aurelia-framework";
import { ValidationHelper } from "helpers/validation-helper";

import routerHelper from "helpers/routerHelper";

import { MaCardOptionsModel, MaCardLineModel, MaCardActionModel } from "components/layout/ma-card/ma-card-options.model";
import { ReceiptOfGoodsSerialNoModel } from "api/models/company/receipt-of-goods-serial-no-model";

import { ReceiptOfGoodsItemModel } from "api/models/company/receipt-of-goods-item-model";
import RouteRepository from "repositories/routeRepository";
import { ReceiptOfGoodsQtyValidation } from "api/enums/receipt-of-goods-qty-validation";

import { NotificationHelper } from "helpers/notification-helper";
import { Router } from "aurelia-router";
import { FormBase } from "pages/form-base";
import { CloneHelper } from "helpers/cloneHelper";
import { CatalogInventoryService } from "services/catalog-inventory-service";

export interface ManageSerialNumbersParameters {
    receiptId: number;
    lineNo: number;
    productCode: string;
    validationMode: string;
    readonly: boolean;
}

@autoinject
export class ManageSerialNumbers extends FormBase {
    @bindable public cards: MaCardOptionsModel[] = [];

    public receiptItem: ReceiptOfGoodsItemModel | null = null;
    public unModified: ReceiptOfGoodsItemModel | null = null;
    public manageSerialNumbersParameters!: ManageSerialNumbersParameters;

    // Cache des verifications "deja en stock" par n° de serie (cle = serie en majuscules) pour eviter
    // de rappeler le backend a chaque saisie. Valeur = true si le n° est deja present en stock pour l'item.
    private stockCheckCache: Map<string, boolean> = new Map<string, boolean>();
    // n° de serie (cle en majuscules) invalides : soit deja en stock, soit en double dans la reception courante.
    private invalidSerialKeys: Set<string> = new Set<string>();

    constructor(i18n: I18N, notificationHelper: NotificationHelper, router: Router,
                private readonly validationHelper: ValidationHelper,
                private readonly receiptOfGoodsService: ReceiptOfGoodsService,
                private readonly catalogInventoryService: CatalogInventoryService,
                private readonly routeRepository: RouteRepository) {
        super(notificationHelper, i18n, router);
    }

    public checkDirty(): boolean {
        if (!this.unModified) {
            return false;
        }

        if (!this.receiptItem) {
            return false;
        }

        const stringifyUnmodified = JSON.stringify(this.unModified).replace(this.regExTakeOnlyNoneAlphanumeric, "");

        const stringifyCurrent = JSON.stringify(this.receiptItem).replace(this.regExTakeOnlyNoneAlphanumeric, "");

        return stringifyUnmodified !== stringifyCurrent;
    }

    public activate(params: any): void {
        this.manageSerialNumbersParameters = {
             receiptId: params.receiptId,
             lineNo: params.lineNo,
             productCode: params.productCode,
             validationMode: params.validationMode,
             readonly: params.readonly === "true"
            };
    }

    public async bind(): Promise<void> {
        this.receiptItem = await this.receiptOfGoodsService.GetReceiptOfGoodsDetail(this.manageSerialNumbersParameters.receiptId!, this.manageSerialNumbersParameters.lineNo, this.manageSerialNumbersParameters.productCode);
        this.unModified = CloneHelper.deepClone(this.receiptItem);
        this.createCardListFromDatasource(this.receiptItem!.SerialNos!);
    }

    public add(): void {
        if (this.validateActiveCard()) {
            this.receiptItem!.SerialNos!.push({ SerialNo: "", ToDelete: false, TransactionNumber: 0 });
            this.refreshCards();
        }
    }

    // Appele en live pendant la frappe (keyup + debounce) : feedback immediat (bordure rouge) sans blur,
    // ce qui evite le "double tap" sur les boutons (la validation n'est plus declenchee par la sortie du champ).
    // Ne reecrit PAS card.model.SerialNo (sinon la saisie live serait perturbee) ; le trim final se fait au save.
    public async serialNoChanged(card: MaCardOptionsModel): Promise<void> {
        const serial = (card.model.SerialNo || "").trim();
        const key = serial.toUpperCase();

        if (serial !== "" && !this.stockCheckCache.has(key)) {
            const inStock = await this.catalogInventoryService.IsSerialNumberInStock(this.receiptItem!.ProductCode || "", serial);
            this.stockCheckCache.set(key, inStock);
        }

        this.recomputeSerialValidity();

        if (serial !== "" && this.invalidSerialKeys.has(key)) {
            const messageKey = this.stockCheckCache.get(key) ? "ReceiptOfGoodsSerialNoAlreadyInStock" : "ReceiptOfGoodsSerialNoDuplicate";
            this.notificationHelper.showError(this.i18n.tr(messageKey));
        }
    }

    public toggleDelete(serialNoItem: ReceiptOfGoodsSerialNoModel): void {
        if (serialNoItem.ToDelete && !this.validateActiveCard()) {
            return;
        }

        serialNoItem.ToDelete = !serialNoItem.ToDelete;
        if (this.isDeletable(serialNoItem)) {
            this.removeDeletedNewSerialItems();
        }

        this.refreshCards();
    }

    public async save(): Promise<void> {
        await this.validateAllSerials();

        if (this.isDataValid()) {
            this.refreshCards();

            const hasItemMarkedForDeletion = this.receiptItem!.SerialNos!.some((serialNoItem: ReceiptOfGoodsSerialNoModel) => {
                return serialNoItem.ToDelete;
            });

            if (hasItemMarkedForDeletion) {
                const dialogMessage = this.i18n.tr("ReceiptOfGoodsConfirmDelete");

                this.notificationHelper.showDialogYesNo(dialogMessage).then(
                    async (success: boolean): Promise<any> => {
                        if (success) {
                            await this.receiptOfGoodsService.SaveReceiptOfGoodsDetail(this.receiptItem);
                            this.unModified = null;
                            routerHelper.navigateToRoute(this.routeRepository.routes.Receipt_Of_Goods_Order_Reception_Edit.name, { receiptId: this.receiptItem!.receiptId });
                        }
                    }
                );
            } else {
                await this.receiptOfGoodsService.SaveReceiptOfGoodsDetail(this.receiptItem);
                this.unModified = null;
                routerHelper.navigateToRoute(this.routeRepository.routes.Receipt_Of_Goods_Order_Reception_Edit.name, { receiptId: this.receiptItem!.receiptId });
            }
        } else {
            setTimeout(() => {
                this.refreshCards();
            }, 3000);
            const messageKey = this.invalidSerialKeys.size > 0 ? "ReceiptOfGoodsSerialNoInvalid" : "ReceiptOfGoodsEmptyItemsValidation";
            this.notificationHelper.showError(this.i18n.tr(messageKey));
        }
    }

    private isDataValid(): boolean {
        let isDataValidFlag = true;

        this.cards.forEach((card: MaCardOptionsModel) => {
            const key = (card.model.SerialNo || "").trim().toUpperCase();

            if (card.model.SerialNo === "" && !card.model.ToDelete) {
                // Flags with a red border invalid cards
                card.class = "border border-danger";
                isDataValidFlag = false;
            } else if (key !== "" && !card.model.ToDelete && this.invalidSerialKeys.has(key)) {
                // n° de serie deja en stock ou en double : bordure rouge + bloque l'enregistrement
                card.class = "border border-danger";
                isDataValidFlag = false;
            }
        });

        return isDataValidFlag;
    }

    // Recalcule l'ensemble des n° de serie invalides a partir de la source (deja en stock via le cache,
    // ou en double dans la reception courante) puis applique le rendu visuel.
    private recomputeSerialValidity(): void {
        const counts = new Map<string, number>();

        this.receiptItem!.SerialNos!
            .filter((serialNoItem: ReceiptOfGoodsSerialNoModel) => !serialNoItem.ToDelete)
            .forEach((serialNoItem: ReceiptOfGoodsSerialNoModel) => {
                const key = (serialNoItem.SerialNo || "").trim().toUpperCase();
                if (key !== "") {
                    counts.set(key, (counts.get(key) || 0) + 1);
                }
            });

        this.invalidSerialKeys.clear();
        counts.forEach((count: number, key: string) => {
            if (count > 1 || this.stockCheckCache.get(key) === true) {
                this.invalidSerialKeys.add(key);
            }
        });

        this.applySerialValidationVisual();
    }

    private applySerialValidationVisual(): void {
        this.cards.forEach((card: MaCardOptionsModel) => {
            const key = (card.model.SerialNo || "").trim().toUpperCase();
            if (key !== "" && !card.model.ToDelete && this.invalidSerialKeys.has(key)) {
                card.class = "border border-danger";
            }
        });
    }

    // Verification faisant autorite avant l'enregistrement : s'assure que CHAQUE n° de serie a ete verifie
    // cote backend (le debounce de la frappe peut ne pas avoir eu le temps de s'executer si l'utilisateur
    // clique tres vite), puis recalcule l'etat de validite. Garantit qu'aucun doublon ne passe au save.
    private async validateAllSerials(): Promise<void> {
        const serialsToCheck: string[] = [];

        this.receiptItem!.SerialNos!
            .filter((serialNoItem: ReceiptOfGoodsSerialNoModel) => !!serialNoItem && !serialNoItem.ToDelete)
            .forEach((serialNoItem: ReceiptOfGoodsSerialNoModel) => {
                const serial = (serialNoItem.SerialNo || "").trim();
                if (serial !== "" && !this.stockCheckCache.has(serial.toUpperCase()) && serialsToCheck.indexOf(serial) === -1) {
                    serialsToCheck.push(serial);
                }
            });

        for (const serial of serialsToCheck) {
            const inStock = await this.catalogInventoryService.IsSerialNumberInStock(this.receiptItem!.ProductCode || "", serial);
            this.stockCheckCache.set(serial.toUpperCase(), inStock);
        }

        this.recomputeSerialValidity();
    }

    private isDeletable(item: ReceiptOfGoodsSerialNoModel): boolean {
        return item.ToDelete && item.TransactionNumber === 0 && this.validationHelper.stringIsNullOrEmpty(item.SerialNo);
    }

    private createCardListFromDatasource(datasource: Array<ReceiptOfGoodsSerialNoModel | null>): void {
        this.cards = [];
        datasource!.forEach((serialNoItem: ReceiptOfGoodsSerialNoModel): void => {
            this.cards.push(this.createCard(serialNoItem));
        });
        this.updateCardsVisual();
    }

    private createCard(serialNoItem: ReceiptOfGoodsSerialNoModel): MaCardOptionsModel {
        return new MaCardOptionsModel({
            model: serialNoItem,
            displayLines: [
                new MaCardLineModel({
                    display: serialNoItem.SerialNo!,
                    class: "font-weight-bold",
                    isCustomTemplate: true,
                }),
            ],
            actionItems: [
                new MaCardActionModel({
                    action: this.toggleDelete.bind(this, serialNoItem),
                }),
            ],
        });
    }

    private removeDeletedNewSerialItems(): void {
        this.receiptItem!.SerialNos! = this.receiptItem!.SerialNos!.filter((serialNoItem: ReceiptOfGoodsSerialNoModel) => {
            return !this.isDeletable(serialNoItem);
        });
    }

    private refreshCards(): void {
        if (this.receiptItem!.SerialNos!.length !== this.cards.length) {
            this.createCardListFromDatasource(this.receiptItem!.SerialNos!);
        } else {
            this.updateCardsVisual();
        }
    }

    private updateCardsVisual(): void {
        this.cards.forEach((card: MaCardOptionsModel) => {
            if (card.model.ToDelete) {
                card.class = "alert-danger";
                card.actionItems![0].icon = "fa fa-undo text-danger";
            } else if (!this.manageSerialNumbersParameters.readonly) {
                card.class = "";
                card.actionItems![0].icon = "fa fa-trash text-danger";
            }
        });

        // Reapplique la bordure rouge sur les n° de serie invalides (la reconstruction ci-dessus a remis class="").
        this.applySerialValidationVisual();
    }

    private validateActiveCard(): boolean {
        const activeCards = this.receiptItem!.SerialNos!.filter((serialNo: ReceiptOfGoodsSerialNoModel) => !serialNo.ToDelete).length;

        const isShowingWarning = this.manageSerialNumbersParameters.validationMode === ReceiptOfGoodsQtyValidation.Block ||
                                 this.manageSerialNumbersParameters.validationMode === ReceiptOfGoodsQtyValidation.Validate;

        const isOverQuantity = activeCards >= this.receiptItem!.InventoryQtyToReceive ||
                               activeCards >= this.receiptItem!.SupplierQtyToReceive;

        if (this.receiptItem!.AddedByUser) { return true; }

        if (isOverQuantity && isShowingWarning) {
            switch (this.manageSerialNumbersParameters.validationMode) {
                case ReceiptOfGoodsQtyValidation.Validate:
                    this.notificationHelper.showWarning(this.i18n.tr("ReceiptOfGoodsInvalidFieldValidate"), "", { timeout: 0});
                    break;

                    case ReceiptOfGoodsQtyValidation.Block:
                    this.notificationHelper.showWarning(this.i18n.tr("ReceiptOfGoodsInvalidFieldBlocked"), "", { timeout: 0});

                    return false;
            }
        }

        return true;
    }
}
